import { create } from "zustand";
import { apiClient } from "@/api/client";
import { AxiosError } from "axios";
import useAuthStore from "@/store/authStore";
import { putToS3WithProgress } from "@/api/storage";

export interface Passwords {
  old_password: string;
  new_password: string;
  confirm_password: string;
}

interface ProfileState {
  editProfile: (profileData: any) => Promise<void>;
  updateProfilePicture: (file: File) => Promise<void>;
}

const useProfileStore = create<ProfileState>((set, get) => ({
  editProfile: async (profileData) => {
    try {
      const response = await apiClient.put(
        `/users/${profileData.id}`,
        profileData
      );
      useAuthStore.getState().setUser(response.data.user);
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.message || "Something went wrong";
      throw new Error(errorMessage);
    }
  },
  updateProfilePicture: async (file: File) => {
    try {
      const ext = (file.name.split(".").pop() || "bin").toLowerCase();
      const mime = file.type || "application/octet-stream";

      // 1. Initiate
      const { data: initData } = await apiClient.post("/users/profile-picture/initiate", {
        ext,
        mime,
        size: file.size,
      });

      if (!initData?.success) throw new Error("Failed to initiate upload.");

      const { url, headers, key } = initData.data;

      // 2. Upload directly to S3
      await putToS3WithProgress(url, headers, file);

      // 3. Confirm
      const { data: confirmData } = await apiClient.post("/users/profile-picture/confirm", {
        key,
        mime,
      });

      if (!confirmData?.success) throw new Error("Failed to confirm upload.");

      // Update local auth store with new URL
      const user = useAuthStore.getState().user;
      if (user) {
        useAuthStore.getState().setUser({
          ...user,
          profile_picture_url_signed: confirmData.data.url,
        });
      }
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage =
        axiosError.response?.data?.message || axiosError.message || "Something went wrong";
      throw new Error(errorMessage);
    }
  },
}));

export default useProfileStore;
